/**
 * Lựa chọn UI của user (tab đang xem, chip lọc, gập/mở section…) lưu ở localStorage.
 *
 * Vì sao cần helper thay vì gọi thẳng `localStorage`:
 * - `localStorage` **ném lỗi** ở một số ngữ cảnh: Safari chế độ riêng tư (quota 0), trình duyệt chặn
 *   site data, iframe cross-origin. Gọi trần trong `onClick` là vỡ handler → nút bấm không ăn, mà
 *   người dùng KHÔNG hiểu vì sao. Đây là tuỳ chọn giao diện — hỏng thì im lặng về mặc định, KHÔNG
 *   được làm hỏng thao tác chính.
 * - `getItem` cũng ném ở cùng ngữ cảnh đó, nên `readPref` cũng phải bọc.
 * - SSR/prerender không có `window` → mọi hàm trả về no-op.
 *
 * ⚠️ CHỈ dùng cho tuỳ chọn hiển thị. Dữ liệu nghiệp vụ (booking, giá, chỉ số…) luôn về Firestore —
 * localStorage là per-thiết-bị, người dùng xoá lúc nào không biết, và KHÔNG được audit.
 *
 * Quy ước key: `gms:{trang}:{tên}:{uid}` (vd `gms:pricing:view:abc123`), hoặc `gms-sec:{tên}:{uid}`
 * cho trạng thái gập/mở section. Luôn kèm uid — máy dùng chung nhiều tài khoản.
 */

const hasStorage = () => typeof window !== 'undefined' && !!window.localStorage;

/** Đọc 1 tuỳ chọn. Trả `null` khi chưa có, hoặc khi trình duyệt chặn storage. */
export function readPref(key: string): string | null {
  if (!hasStorage()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;   // private mode / site data bị chặn — coi như chưa từng lưu
  }
}

/** Ghi 1 tuỳ chọn. Nuốt lỗi có chủ đích: hỏng persist KHÔNG được chặn thao tác user đang làm. */
export function writePref(key: string, value: string): void {
  if (!hasStorage()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* quota/private mode — phiên này không nhớ được, chấp nhận */
  }
}

/** Xoá 1 tuỳ chọn (dùng khi user về lại mặc định — đỡ rác localStorage). */
export function removePref(key: string): void {
  if (!hasStorage()) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* như trên */
  }
}

/** Ghi/xoá theo cờ bật-tắt: `true` → ghi `value`, `false` → xoá hẳn key. */
export function writePrefFlag(key: string, on: boolean, value = '1'): void {
  if (on) writePref(key, value); else removePref(key);
}

/** Đọc tuỳ chọn dạng JSON. `fallback` khi chưa có / JSON hỏng (key cũ đổi schema) / storage bị chặn. */
export function readPrefJson<T>(key: string, fallback: T): T {
  const raw = readPref(key);
  if (raw == null) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;   // dữ liệu cũ sai schema — đừng làm vỡ trang
  }
}

/** Ghi tuỳ chọn dạng JSON. */
export function writePrefJson(key: string, value: unknown): void {
  try {
    writePref(key, JSON.stringify(value));
  } catch {
    /* value có vòng lặp tham chiếu — không thể serialize, bỏ qua */
  }
}
